// Item drop/roll generation pipeline. See docs/adr/0019-item-drop-roll.md.
//
// `generateItem(ctx, rng)` is a deterministic 7-stage pure function turning a
// drop context into one `Item`:
//
//   [1] rollRarity     基礎重み × ダンジョン Tier 補正 × luck.rarityBonus
//   [2] rollIlvl       [difficultyIlvl × 0.8, difficultyIlvl] 帯で floor 抽選
//   [3] selectBase     ilvl 帯 → base Tier (境界 15% 漏れ) → 候補をフラット抽選
//   [4] rollAffixes    rarity 別個数 → 候補プールフィルタ → Tier roll
//   [5] rollItemSkills rarity 別枠数 → ITEM_SKILLS フラット抽選 → Tier roll
//   [6] resolveSetId   rarity === 'set' のとき SET_DEFINITIONS から抽選
//   [7] Item 組立      behaviorRule 抽選 + id 付与 + Item interface へ格納
//
// rarity === 'unique' は [3]-[6] を使わず unique-item catalogue (resolveUnique) から
// 固定ベース + 固有 affix + 固有 itemSkill を組む (ADR 0022、buildUniqueItem)。
//
// 各段関数は §J 構造テストで個別検証するため individually export する。
// 同一 ctx + 同一 seed → 同一 Item (決定論)。
//
// rarity 重み / Tier 補正マトリクス / 境界漏れ率 / affix Tier 相対重みは本
// モジュールの定数テーブル。数値は全て暫定で Phase 4 sim ベンチで校正する。
//
// behaviorRule は slot → category 固定写像 (ADR 0020、behavior-rules.ts) に基づき
// 抽選する。カテゴリを持たない weaponMain / offHand / belt / cloak は付与しない。
import { MOVEMENT_RULES, POSITION_POLICY_RULES, RARITIES, TARGETING_RULES, TEMPO_RULES, THREAT_POLICY_RULES, } from '@renavora/shared';
import { ITEM_SKILL_TIER_VALUES } from '../skills/item-skill-values.js';
import { ITEM_SKILLS, ITEM_SKILL_IDS } from '../skills/item-skills.js';
import { AFFIX_DEFINITIONS } from './affixes.js';
import { ITEM_BASES } from './base-items.js';
import { DAMAGE_REACT_CATALOGUE, SLOT_BEHAVIOR_CATEGORY } from './behavior-rules.js';
import { SET_DEFINITIONS } from './sets.js';
import { UNIQUE_ITEMS } from './unique-items.js';
// =============================================================================
// 定数テーブル (Phase 4 sim ベンチ校正対象、ADR 0019 §2 / §3 / §4)
// =============================================================================
/** rarity 基礎重み (通常戦闘)。ADR 0019 §2。 */
const RARITY_BASE_WEIGHTS = {
    normal: 56,
    magic: 28,
    rare: 12,
    unique: 3,
    set: 1,
};
/** ダンジョン Tier 補正 (乗算)。Tier は 1-5 にクランプ。ADR 0019 §2。 */
const RARITY_TIER_MULTIPLIER = {
    1: { normal: 1.25, magic: 1.0, rare: 0.75, unique: 0.5, set: 0.4 },
    2: { normal: 1.1, magic: 1.0, rare: 0.95, unique: 0.8, set: 0.7 },
    3: { normal: 0.95, magic: 1.0, rare: 1.1, unique: 1.1, set: 1.0 },
    4: { normal: 0.8, magic: 0.95, rare: 1.25, unique: 1.35, set: 1.25 },
    5: { normal: 0.65, magic: 0.9, rare: 1.35, unique: 1.6, set: 1.5 },
};
/** luck.rarityBonus が底上げする rarity (rare 以上)。ADR 0019 §2。 */
const LUCK_BOOSTED_RARITIES = new Set(['rare', 'unique', 'set']);
/** ilvl × 0.8 が ilvl 帯の下限。ADR 0019 §3。 */
const ILVL_BAND_FLOOR_RATIO = 0.8;
/**
 * base Tier 境界漏れ率。写像した Tier から隣接 Tier へ漏れる確率。ADR 0019 §3。
 * 端 Tier (T1/T5) は片側のみ漏れる。
 */
const TIER_LEAK_CHANCE = 0.15;
/** base Tier ↔ ilvl 帯の半開区間規約 (docs/04 §4.3.1、ADR 0019 §3)。 */
const TIER_ILVL_UPPER_BOUNDS = [
    { tier: 1, below: 30 },
    { tier: 2, below: 60 },
    { tier: 3, below: 120 },
    { tier: 4, below: 400 },
];
/**
 * Tier 抽選の相対重み (targetTier 中心)。`offset` は targetTier からの差。
 * qualityBonus は上位 Tier (offset ≥ 1) の重みを底上げする。ADR 0019 §4.3。
 */
const TIER_REL_WEIGHTS = [
    { offset: -1, weight: 20 },
    { offset: 0, weight: 55 },
    { offset: 1, weight: 20 },
    { offset: 2, weight: 5 },
];
/** rare 装備の affix 合計数。prefix 1-3 抽選、残りを suffix。ADR 0019 §4.1。 */
const RARE_AFFIX_TOTAL = 6;
/** set 装備の affix 合計数。prefix 1-2 抽選、残りを suffix。ADR 0019 §4.1。 */
const SET_AFFIX_TOTAL = 4;
/** rare 装備の itemSkill 枠数とその抽選重み。ADR 0019 §5。 */
const RARE_ITEM_SKILL_COUNTS = [1, 2, 3];
const RARE_ITEM_SKILL_WEIGHTS = [60, 30, 10];
const SET_DEFINITION_BY_ID = new Map(SET_DEFINITIONS.map((def) => [def.id, def]));
const ITEM_BASE_BY_ID = new Map(ITEM_BASES.map((base) => [base.id, base]));
const AFFIX_DEFINITION_BY_ID = new Map(AFFIX_DEFINITIONS.map((def) => [def.id, def]));
// =============================================================================
// 内部ヘルパー
// =============================================================================
/** 重み付き抽選。weights は items と同長・非負・合計 > 0 を前提。 */
function weightedPick(items, weights, rng) {
    let total = 0;
    for (const w of weights)
        total += w;
    let roll = rng.nextFloat() * total;
    for (let i = 0; i < items.length; i++) {
        roll -= weights[i];
        if (roll < 0)
            return items[i];
    }
    return items[items.length - 1];
}
/** ダンジョン Tier を 1-5 の `ItemBaseTier` にクランプ。ADR 0019 §2。 */
function clampDungeonTier(dungeonTier) {
    const t = Math.floor(dungeonTier);
    if (t <= 1)
        return 1;
    if (t >= 5)
        return 5;
    return t;
}
/** ilvl を base Tier へ写像。半開区間 [min, max)、端は片側クランプ。ADR 0019 §3。 */
function ilvlToBaseTier(ilvl) {
    for (const { tier, below } of TIER_ILVL_UPPER_BOUNDS) {
        if (ilvl < below)
            return tier;
    }
    return 5;
}
/**
 * 写像 Tier に境界漏れを適用。15% で隣接 Tier へ漏れる。
 * T1 は上 (T2) のみ、T5 は下 (T4) のみ。中間 Tier は上下を一様抽選。ADR 0019 §3。
 */
function applyTierLeak(mappedTier, rng) {
    if (!rng.nextBool(TIER_LEAK_CHANCE))
        return mappedTier;
    if (mappedTier === 1)
        return 2;
    if (mappedTier === 5)
        return 4;
    return rng.nextBool() ? (mappedTier - 1) : (mappedTier + 1);
}
/**
 * targetTier 中心の相対重みで Tier を抽選。返り値は [minTier, maxTier] の整数。
 * 存在する Tier のみで正規化。qualityBonus は上位 Tier (offset ≥ 1) の重みに
 * `weight × qualityBonus` 分を加算シフトする (上方シフト)。ADR 0019 §4.3。
 */
function rollRelativeTier(targetTier, minTier, maxTier, qualityBonus, rng) {
    const tiers = [];
    const weights = [];
    for (const { offset, weight } of TIER_REL_WEIGHTS) {
        const tier = targetTier + offset;
        if (tier < minTier || tier > maxTier)
            continue;
        tiers.push(tier);
        weights.push(offset >= 1 ? weight + weight * qualityBonus : weight);
    }
    return weightedPick(tiers, weights, rng);
}
/** rarity 別の prefix / suffix 個数を決定。rare / set は prefix 数を抽選。ADR 0019 §4.1。 */
function rollAffixCounts(rarity, rng) {
    switch (rarity) {
        case 'magic':
            return { prefix: 1, suffix: 1 };
        case 'rare': {
            const prefix = 1 + rng.nextInt(3);
            return { prefix, suffix: RARE_AFFIX_TOTAL - prefix };
        }
        case 'set': {
            const prefix = 1 + rng.nextInt(2);
            return { prefix, suffix: SET_AFFIX_TOTAL - prefix };
        }
        case 'normal':
        case 'unique':
            // normal は affix なし。unique は unique-item catalogue 固定 (本 ADR は hook のみ)。
            return { prefix: 0, suffix: 0 };
    }
}
/** 候補プールフィルタ: kind 一致 + slot 適用可 + baseKind 適用可。ADR 0019 §4.2。 */
function filterAffixPool(kind, base) {
    return AFFIX_DEFINITIONS.filter((def) => {
        if (def.kind !== kind)
            return false;
        if (!def.applicableSlots.includes(base.slot))
            return false;
        if (def.applicableBaseKinds !== undefined && !def.applicableBaseKinds.includes(base.baseKind)) {
            return false;
        }
        return true;
    });
}
/**
 * プールから `count` 個の affix 定義を重複なし抽選。同一 family は付与済み集合
 * `usedFamilies` で 1 アイテムにつき 1 個に制限。プール枯渇時は付与可能分のみ。
 * ADR 0019 §4.2。
 */
function pickAffixDefs(pool, count, usedFamilies, rng) {
    const remaining = pool.filter((def) => !usedFamilies.has(def.family));
    const picked = [];
    while (picked.length < count && remaining.length > 0) {
        const def = remaining[rng.nextInt(remaining.length)];
        picked.push(def);
        usedFamilies.add(def.family);
        for (let i = remaining.length - 1; i >= 0; i--) {
            if (remaining[i].family === def.family)
                remaining.splice(i, 1);
        }
    }
    return picked;
}
/**
 * 抽選 Tier の range から値を一様 (整数) 抽選。ADR 0019 §4.3。
 * Tier 昇格 / シャード適用 (ADR 0023) の値再抽選でも再利用するため export する。
 */
export function rollAffixValue(range, rng) {
    return range.min + rng.nextInt(range.max - range.min + 1);
}
/** 決定論的な Item インスタンス id を生成。同 rng 位置 → 同 id。ADR 0019 §1-[7]。 */
function makeItemId(base, rng) {
    return `item.${base.baseKind}.${rng.nextU32().toString(16).padStart(8, '0')}`;
}
/** rarity 別の itemSkill 枠数。rare は 60/30/10 で 1-3、set は一様 1-2。ADR 0019 §5。 */
function rollItemSkillCount(rarity, rng) {
    switch (rarity) {
        case 'rare':
            return weightedPick(RARE_ITEM_SKILL_COUNTS, RARE_ITEM_SKILL_WEIGHTS, rng);
        case 'set':
            return 1 + rng.nextInt(2);
        case 'normal':
        case 'magic':
        case 'unique':
            // normal / magic は itemSkill なし。unique は catalogue 固定 (本 ADR は hook のみ)。
            return 0;
    }
}
// =============================================================================
// 生成パイプライン
// =============================================================================
/**
 * Generate one `Item` from a drop context. 7-stage deterministic pure function
 * (docs/adr/0019 §1)。同一 ctx + 同一 seed → 同一 `Item`。
 *
 * `behaviorRule` は slot → category 写像で抽選する (ADR 0020)。カテゴリを持たない
 * weaponMain / offHand / belt / cloak のアイテムは behaviorRule を持たない。
 */
export function generateItem(ctx, rng) {
    const rarity = rollRarity(ctx, rng);
    const ilvl = rollIlvl(ctx, rng);
    // unique は通常の affix/itemSkill 抽選を使わず unique-item catalogue から組む。
    if (rarity === 'unique')
        return buildUniqueItem(ilvl, ctx.luck, rng);
    const setId = rarity === 'set' ? resolveSetId(rng) : undefined;
    const base = selectBase(ilvl, rng, setId);
    const affixes = rollAffixes(base, rarity, ilvl, ctx.luck, rng);
    const itemSkills = rollItemSkills(rarity, ilvl, ctx.luck, rng);
    const behaviorRule = rollBehaviorRule(base.slot, rng);
    const id = makeItemId(base, rng);
    return {
        id,
        baseId: base.id,
        slot: base.slot,
        ilvl,
        rarity,
        typeAffinity: base.typeAffinity,
        affixes,
        itemSkills,
        ...(behaviorRule !== undefined ? { behaviorRule } : {}),
        ...(setId !== undefined ? { setId } : {}),
    };
}
/**
 * unique rarity の Item を unique-item catalogue から組む (ADR 0022)。
 * `resolveUnique` で `UniqueItemDefinition` を抽選し、固定ベース + 固有 affix
 * (値を tier range から抽選、`source: 'unique'`) + 固有 itemSkill (tier を抽選) を
 * 組み立てる。affix 値と itemSkill tier の抽選が個体差 (docs/04 §4.4)。
 */
function buildUniqueItem(ilvl, luck, rng) {
    const def = resolveUnique(rng);
    const base = ITEM_BASE_BY_ID.get(def.baseId);
    if (base === undefined) {
        throw new Error(`buildUniqueItem: unknown baseId ${def.baseId} (unique ${def.id})`);
    }
    const affixes = def.affixes.map((spec) => {
        const affixDef = AFFIX_DEFINITION_BY_ID.get(spec.affixId);
        if (affixDef === undefined) {
            throw new Error(`buildUniqueItem: unknown affixId ${spec.affixId} (unique ${def.id})`);
        }
        return {
            id: spec.affixId,
            tier: spec.tier,
            values: [rollAffixValue(affixDef.valuesByTier[spec.tier], rng)],
            kind: affixDef.kind,
            source: 'unique',
        };
    });
    const qualityBonus = luck?.qualityBonus ?? 0;
    const targetTier = ilvlToBaseTier(ilvl) - 1;
    const itemSkills = def.itemSkillIds.map((skillId) => {
        const template = ITEM_SKILLS[skillId];
        if (template === undefined) {
            throw new Error(`buildUniqueItem: unknown itemSkill ${skillId} (unique ${def.id})`);
        }
        const tier = rollRelativeTier(targetTier, 0, 4, qualityBonus, rng);
        return {
            templateId: template.id,
            kind: template.kind,
            activation: template.activation,
            values: [...(ITEM_SKILL_TIER_VALUES[template.id] ?? [])],
            tier,
        };
    });
    const behaviorRule = rollBehaviorRule(base.slot, rng);
    const id = makeItemId(base, rng);
    return {
        id,
        baseId: base.id,
        slot: base.slot,
        ilvl,
        rarity: 'unique',
        typeAffinity: base.typeAffinity,
        affixes,
        itemSkills,
        uniqueId: def.id,
        ...(behaviorRule !== undefined ? { behaviorRule } : {}),
    };
}
/**
 * [1] rarity 抽選。基礎重み × ダンジョン Tier 補正 × luck.rarityBonus。ADR 0019 §2。
 * `ctx.forcedRarity` 指定時は抽選せずその値を返す。
 */
export function rollRarity(ctx, rng) {
    if (ctx.forcedRarity !== undefined)
        return ctx.forcedRarity;
    const tierMult = RARITY_TIER_MULTIPLIER[clampDungeonTier(ctx.dungeonTier)];
    const rarityBonus = ctx.luck?.rarityBonus ?? 0;
    const weights = RARITIES.map((rarity) => {
        const base = RARITY_BASE_WEIGHTS[rarity] * tierMult[rarity];
        return LUCK_BOOSTED_RARITIES.has(rarity) ? base * (1 + rarityBonus) : base;
    });
    return weightedPick(RARITIES, weights, rng);
}
/**
 * [2] ilvl 決定。`[floor(difficultyIlvl × 0.8), difficultyIlvl]` の整数帯で floor
 * 抽選し difficultyIlvl で上限クランプ。`ilvl ≤ difficultyIlvl` 厳守。ADR 0019 §3。
 * ilvl は整数のため difficultyIlvl を floor して帯を整数化する。
 */
export function rollIlvl(ctx, rng) {
    const max = Math.floor(ctx.difficultyIlvl);
    const min = Math.floor(max * ILVL_BAND_FLOOR_RATIO);
    const span = max - min + 1;
    const ilvl = min + rng.nextInt(span);
    return Math.min(ilvl, max);
}
/**
 * [3] base 選択。ilvl 帯 → base Tier (境界 15% 漏れ) → 当該 Tier の候補を
 * フラット (一様) 抽選。`setId` 指定時は当該セットの `slots` に入る base に限定。
 * ADR 0019 §3。
 */
export function selectBase(ilvl, rng, setId) {
    const tier = applyTierLeak(ilvlToBaseTier(ilvl), rng);
    let candidates = ITEM_BASES.filter((base) => base.tier === tier);
    if (setId !== undefined) {
        const def = SET_DEFINITION_BY_ID.get(setId);
        if (def === undefined) {
            throw new Error(`selectBase: unknown setId ${setId}`);
        }
        const slots = new Set(def.slots);
        candidates = candidates.filter((base) => slots.has(base.slot));
    }
    if (candidates.length === 0) {
        throw new Error(`selectBase: no base for tier ${tier}${setId !== undefined ? ` set ${setId}` : ''}`);
    }
    return rng.pick(candidates);
}
/**
 * [4] affix roll。rarity 別個数 → 候補プールフィルタ → 各 affix の Tier roll。
 * targetTier は ilvl 帯から決まり、`Affix.tier` を pin して `valuesByTier` の
 * range 内で値を一様抽選する。`source: 'rolled'`。ADR 0019 §4。
 *
 * normal は affix なし。unique は unique-item catalogue 固定のため空配列を返す
 * (本 ADR は unique 分岐の hook のみ、§0.3)。
 */
export function rollAffixes(base, rarity, ilvl, luck, rng) {
    const counts = rollAffixCounts(rarity, rng);
    if (counts.prefix === 0 && counts.suffix === 0)
        return [];
    const qualityBonus = luck?.qualityBonus ?? 0;
    const targetTier = ilvlToBaseTier(ilvl);
    const usedFamilies = new Set();
    const affixes = [];
    for (const kind of ['prefix', 'suffix']) {
        const count = kind === 'prefix' ? counts.prefix : counts.suffix;
        if (count === 0)
            continue;
        const pool = filterAffixPool(kind, base);
        for (const def of pickAffixDefs(pool, count, usedFamilies, rng)) {
            const tier = rollRelativeTier(targetTier, 1, 5, qualityBonus, rng);
            affixes.push({
                id: def.id,
                tier,
                values: [rollAffixValue(def.valuesByTier[tier], rng)],
                kind: def.kind,
                source: 'rolled',
            });
        }
    }
    return affixes;
}
/**
 * 単一 affix のロール。`craftEnchant` (ADR 0023) が枠上限を超えて affix を 1 つ
 * 付与するために使う。指定 kind の候補プールから `usedFamilies` に無い family の
 * affix 定義を 1 つ抽選し、Tier を §4.3 の相対重みで roll、値を tier range から
 * 一様抽選する (`source: 'rolled'`)。`usedFamilies` は変異させ family 重複を防ぐ。
 * プール枯渇時は `undefined`。
 *
 * drop の `rollAffixes` は def を一括 pick してから値を一括 roll する RNG 消費順の
 * ため本関数経由には書き換えていない (drop の決定論結果を保つ)。
 */
export function rollAffix(base, kind, usedFamilies, ilvl, rng) {
    const pool = filterAffixPool(kind, base);
    const picked = pickAffixDefs(pool, 1, usedFamilies, rng);
    if (picked.length === 0)
        return undefined;
    const def = picked[0];
    const tier = rollRelativeTier(ilvlToBaseTier(ilvl), 1, 5, 0, rng);
    return {
        id: def.id,
        tier,
        values: [rollAffixValue(def.valuesByTier[tier], rng)],
        kind: def.kind,
        source: 'rolled',
    };
}
/**
 * [5] itemSkill roll の機構。rarity 別枠数 → `ITEM_SKILLS` をフラット抽選
 * (重複テンプレートなし) → `ItemSkill.tier` (0-4) を §4.3 と同じ相対重みで roll。
 * ADR 0019 §5。
 *
 * `ItemSkill.values` は `ITEM_SKILL_TIER_VALUES` (ADR 0021 / Phase 5.x-A2) を
 * 参照して格納する。proc / cdr / passive stat/shield/regen は 5 段 Tier 値、
 * それ以外の未配線チャネルは空配列。normal / magic は枠 0、unique は catalogue
 * 固定 (hook のみ)。
 */
export function rollItemSkills(rarity, ilvl, luck, rng) {
    const count = rollItemSkillCount(rarity, rng);
    if (count === 0)
        return [];
    const used = new Set();
    const itemSkills = [];
    for (let i = 0; i < count; i++) {
        const skill = rollItemSkill(ilvl, used, luck, rng);
        if (skill === undefined)
            break;
        itemSkills.push(skill);
        used.add(skill.templateId);
    }
    return itemSkills;
}
/**
 * 単一 itemSkill のロール。`ITEM_SKILLS` から `excludeTemplateIds` に無い template を
 * フラット (一様) 抽選し、`ItemSkill.tier` (0-4) を §4.3 と同じ相対重みで roll する。
 * `values` は `ITEM_SKILL_TIER_VALUES` (ADR 0021) を参照。候補枯渇時は `undefined`。
 *
 * drop の `rollItemSkills` と `craftRerollItemSkill` (ADR 0023) が共有する。
 * `rollItemSkills` のループ本体抽出のため RNG 消費順は従来と等価 (既存 loot テストで担保)。
 */
export function rollItemSkill(ilvl, excludeTemplateIds, luck, rng) {
    const candidates = ITEM_SKILL_IDS.filter((id) => !excludeTemplateIds.has(id));
    if (candidates.length === 0)
        return undefined;
    const id = candidates[rng.nextInt(candidates.length)];
    const template = ITEM_SKILLS[id];
    const qualityBonus = luck?.qualityBonus ?? 0;
    // itemSkill の tier は 0-4 (= T1-T5)。ilvl 帯 Tier (1-5) を 0 起点へ写像。
    const targetTier = ilvlToBaseTier(ilvl) - 1;
    const tier = rollRelativeTier(targetTier, 0, 4, qualityBonus, rng);
    return {
        templateId: template.id,
        kind: template.kind,
        activation: template.activation,
        // Tier 値表を参照。アイテム間で配列参照を共有しないようコピー。
        values: [...(ITEM_SKILL_TIER_VALUES[template.id] ?? [])],
        tier,
    };
}
/**
 * [6] set 付与。`SET_DEFINITIONS` から setId をフラット (一様) 抽選。ADR 0019 §6。
 * 専用 set ベースは作らず、`selectBase` 側で当該セットの `slots` 制約をかける。
 */
export function resolveSetId(rng) {
    return rng.pick(SET_DEFINITIONS).id;
}
/**
 * rarity === 'unique' のとき `UNIQUE_ITEMS` から `UniqueItemDefinition` をフラット
 * (一様) 抽選。テーマ別重み付けは別 ADR (ADR 0022 Scope A は機構検証優先で一様)。
 */
export function resolveUnique(rng) {
    return rng.pick(UNIQUE_ITEMS);
}
/**
 * [7] behaviorRule 抽選。slot → category 固定写像 (`SLOT_BEHAVIOR_CATEGORY`) で
 * カテゴリを決め、当該カテゴリのルールプールからフラット (一様) 抽選する。ADR 0020。
 * カテゴリを持たない weaponMain / offHand / belt / cloak は `undefined` を返す
 * (生成 Item は behaviorRule を省略)。
 */
export function rollBehaviorRule(slot, rng) {
    const category = SLOT_BEHAVIOR_CATEGORY[slot];
    if (category === undefined)
        return undefined;
    switch (category) {
        case 'targeting':
            return { category, ruleId: rng.pick(TARGETING_RULES) };
        case 'movement':
            return { category, ruleId: rng.pick(MOVEMENT_RULES) };
        case 'tempo':
            return { category, ruleId: rng.pick(TEMPO_RULES) };
        case 'threatPolicy':
            return { category, ruleId: rng.pick(THREAT_POLICY_RULES) };
        case 'positionPolicy':
            return { category, ruleId: rng.pick(POSITION_POLICY_RULES) };
        case 'damageReact': {
            // catalogue オブジェクトをアイテム間 / catalogue 本体と共有しないようディープコピー。
            const picked = rng.pick(DAMAGE_REACT_CATALOGUE);
            return {
                category,
                rule: {
                    ...picked,
                    trigger: { ...picked.trigger },
                    overrides: picked.overrides.map((o) => ({ ...o })),
                },
            };
        }
        default: {
            // RollableBehaviorCategory に値を追加した際にコンパイル時 fail させる網羅性ガード。
            const exhaustive = category;
            throw new Error(`rollBehaviorRule: unreachable category ${String(exhaustive)}`);
        }
    }
}
//# sourceMappingURL=loot.js.map